import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { LancamentoEntreConstasService } from '../../../shared/services/api/Financeiro/LancamentoEntreConstas';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

// Transferência do Caixa para o Cofre a partir da Conciliação, inclusive de
// um dia anterior: o lançamento cai NO DIA DO CAIXA pesquisado (`data`), não
// no dia de hoje — o caixa daquele dia fecha certo —, e o histórico guarda
// quando a transferência foi feita de verdade (ver
// TransferenciaCaixaCofre.ts, backend). O valor já vem com todo o saldo do
// caixa no fim daquele dia; dá pra diminuir se for transferir só uma parte.
interface IDialogTransferenciaCaixaCofreProps {
  open: boolean;
  idcaixa: string;
  nomeCaixa?: string;
  data: string; // AAAA-MM-DD — dia do caixa pesquisado
  onClose: (transferiu?: boolean) => void;
}

type TContaOpcao = { idconta: string; label: string };

export const DialogTransferenciaCaixaCofre: React.FC<IDialogTransferenciaCaixaCofreProps> = ({ open, idcaixa, nomeCaixa, data, onClose }) => {
  const [cofres, setCofres] = useState<TContaOpcao[]>([]);
  const [cofre, setCofre] = useState<TContaOpcao | null>(null);
  const [saldo, setSaldo] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [valorCentavos, setValorCentavos] = useState('');
  const [observacao, setObservacao] = useState('');
  const [transferindo, setTransferindo] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'success' | 'error' | 'warning'; texto: string } | null>(null);

  const dataBR = data ? dayjs(data).format('DD/MM/YYYY') : '';
  const ehHoje = data === dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    if (!open || !idcaixa || !data) return;
    setCofre(null);
    setSaldo(null);
    setValorCentavos('');
    setObservacao('');
    setCarregando(true);
    Promise.all([
      LancamentoEntreConstasService.saldoCaixaNaData(idcaixa, data),
      ContaBancariaService.getAllList('', '', 'cofre', 20),
    ]).then(([saldoDia, contas]) => {
      setCarregando(false);
      if (saldoDia instanceof Error) {
        setMensagem({ tipo: 'error', texto: saldoDia.message });
      } else {
        setSaldo(saldoDia);
        // Já sugere transferir tudo o que o caixa tinha no fim do dia.
        setValorCentavos(saldoDia > 0 ? String(Math.round(saldoDia * 100)) : '');
      }
      if (!(contas instanceof Error)) {
        const opcoes = contas.data.map((c) => ({ idconta: c.idconta, label: [c.numeroconta, c.titular].filter(Boolean).join(' — ') }));
        setCofres(opcoes);
        if (opcoes.length === 1) setCofre(opcoes[0]);
      }
    });
  }, [open, idcaixa, data]);

  const valor = valorCentavos ? Number(valorCentavos) / 100 : 0;
  const fecharMensagem = (_?: React.SyntheticEvent | Event, motivo?: SnackbarCloseReason) => {
    if (motivo === 'clickaway') return;
    const sucesso = mensagem?.tipo === 'success';
    setMensagem(null);
    if (sucesso) onClose(true);
  };

  const confirmar = () => {
    if (!cofre) { setMensagem({ tipo: 'warning', texto: 'Selecione o cofre de destino' }); return; }
    if (!(valor > 0)) { setMensagem({ tipo: 'warning', texto: 'Informe o valor a transferir' }); return; }
    setTransferindo(true);
    LancamentoEntreConstasService.transferirCaixaParaCofre({
      idcaixa, idcofre: cofre.idconta, data, valor, observacao: observacao.trim(),
    }).then((resultado) => {
      setTransferindo(false);
      if (resultado instanceof Error) { setMensagem({ tipo: 'error', texto: resultado.message }); return; }
      setMensagem({ tipo: 'success', texto: `${formatarMoeda(resultado.valor)} transferido para o cofre no caixa de ${dataBR}.` });
    });
  };

  return (
    <Dialog open={open} onClose={() => !transferindo && onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Transferir para o Cofre{nomeCaixa ? ` — ${nomeCaixa}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={!!mensagem} autoHideDuration={mensagem?.tipo === 'error' ? null : 2500} onClose={fecharMensagem}>
          <Alert onClose={fecharMensagem} severity={mensagem?.tipo || 'info'} variant="filled" sx={{ width: '100%' }}>{mensagem?.texto}</Alert>
        </Snackbar>

        <Alert severity="info" sx={{ mb: 2 }}>
          O lançamento cai no caixa de <b>{dataBR}</b>{ehHoje ? '' : ' (dia pesquisado na conciliação)'}.
          {!ehHoje && <> No histórico fica registrado que a transferência foi feita em {dayjs().format('DD/MM/YYYY')}.</>}
        </Alert>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">Saldo do caixa no fim do dia</Typography>
          {carregando ? <CircularProgress size={18} /> : (
            <Typography variant="subtitle1" fontWeight={700} color={saldo && saldo > 0 ? 'success.main' : 'text.secondary'}>
              {formatarMoeda(saldo ?? 0)}
            </Typography>
          )}
        </Box>

        <Autocomplete
          openText="Abrir" closeText="Fechar" noOptionsText="Nenhum cofre encontrado" loadingText="Carregando..."
          disablePortal options={cofres} loading={carregando} disabled={transferindo} value={cofre}
          isOptionEqualToValue={(a, b) => a.idconta === b.idconta}
          onChange={(_, novo) => setCofre(novo)}
          renderInput={(params) => <TextField {...params} size="small" label="Cofre de destino" />}
          sx={{ mb: 2 }}
        />

        <Box display="flex" gap={1} alignItems="center" mb={2}>
          <TextField
            fullWidth size="small" label="Valor a transferir" placeholder="0,00"
            value={valor ? formatarMoeda(valor) : ''}
            onChange={(e) => setValorCentavos(e.target.value.replace(/\D/g, ''))}
            inputProps={{ inputMode: 'numeric' }}
            disabled={transferindo}
          />
          <Button size="small" variant="text" sx={{ whiteSpace: 'nowrap' }} disabled={transferindo || !saldo || saldo <= 0}
            onClick={() => setValorCentavos(String(Math.round((saldo || 0) * 100)))}>
            Todo o saldo
          </Button>
        </Box>

        <TextField fullWidth size="small" label="Observação" placeholder="Opcional" value={observacao}
          onChange={(e) => setObservacao(e.target.value)} disabled={transferindo} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={() => onClose(false)} disabled={transferindo}>Cancelar</Button>
        <Button variant="contained" color="success" disableElevation onClick={confirmar} disabled={transferindo || carregando}
          startIcon={transferindo ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {transferindo ? 'Transferindo...' : 'Transferir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
